import React, { useState } from "react";
import { Colors } from "../../Constants/colors";
import { FontFamily } from "../../Constants/Enums/FontFamily.enums";
import {
  ALIGN_ITEMS,
  DISPLAY,
  FONT_SIZE,
  FONT_WEIGHT,
  SPACING,
} from "../../Constants/Enums/styles.enum";
import openEye from "../../images/eye-empty.svg";
import closeEye from "../../images/eye-password-hide.svg";

const ERROR_TINT = "#FFF6F6";
const ERROR_RING = "rgba(255, 0, 0, 0.12)";
const ICON_GUTTER = 46;
const ACTION_GUTTER = 48;

interface Props {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  isPassword?: boolean;
  autoComplete?: string;
  errorMessage?: string | null;
  disabled?: boolean;
  renderIcon?: (color: string) => React.ReactNode;
  inputRef?: React.Ref<HTMLInputElement>;
}

const ErrorIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill={Colors.Tomato} fillOpacity="0.14" />
    <path
      d="M12 7.5V13"
      stroke={Colors.Tomato}
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="12" cy="16.25" r="1.1" fill={Colors.Tomato} />
  </svg>
);

const AuthInput = ({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  isPassword = false,
  autoComplete,
  errorMessage = null,
  disabled = false,
  renderIcon,
  inputRef,
}: Props) => {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  const hasError = Boolean(errorMessage);
  const errorId = `${id}-error`;

  const iconColor = hasError
    ? Colors.Tomato
    : isFocused
      ? Colors.TangerineOrange
      : Colors.Grey;

  const borderColor = hasError
    ? Colors.Tomato
    : isFocused
      ? Colors.TangerineOrange
      : Colors.LightGrey;

  const ringColor = hasError
    ? ERROR_RING
    : isFocused
      ? Colors.LIGHT_TANGERINE_ORANGE
      : null;

  const resolvedType = isPassword && !isPasswordVisible ? "password" : type;

  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: DISPLAY.BLOCK,
          marginBottom: SPACING.S8,
          fontSize: FONT_SIZE.F13,
          fontFamily: FontFamily.POPPINS_REGULAR,
          fontWeight: FONT_WEIGHT.MEDIUM,
          color: hasError ? Colors.Tomato : Colors.DarkGrey,
          letterSpacing: "0.2px",
          transition: "color 0.2s ease",
        }}
      >
        {label}
      </label>

      <div
        style={{
          position: "relative",
          display: DISPLAY.FLEX,
          alignItems: ALIGN_ITEMS.CENTER,
        }}
      >
        {renderIcon ? (
          <span
            style={{
              position: "absolute",
              left: SPACING.S16,
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
              pointerEvents: "none",
              transition: "opacity 0.2s ease",
            }}
          >
            {renderIcon(iconColor)}
          </span>
        ) : null}

        <input
          id={id}
          name={name}
          className="auth-input"
          type={resolvedType}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
          ref={inputRef}
          onChange={(event) => onChange(event.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          style={{
            width: "100%",
            height: "54px",
            boxSizing: "border-box",
            paddingTop: SPACING.S0,
            paddingBottom: SPACING.S0,
            paddingLeft: renderIcon ? `${ICON_GUTTER}px` : SPACING.S16,
            paddingRight: isPassword ? `${ACTION_GUTTER}px` : SPACING.S16,
            borderRadius: "14px",
            border: `1.5px solid ${borderColor}`,
            backgroundColor: hasError
              ? ERROR_TINT
              : isFocused
                ? Colors.White
                : Colors.WhiteSmoke,
            boxShadow: ringColor ? `0 0 0 4px ${ringColor}` : "none",
            color: Colors.Black,
            fontFamily: FontFamily.ROBOTO_REGULAR,
            fontSize: FONT_SIZE.F16,
            outline: "none",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />

        {isPassword ? (
          <button
            type="button"
            aria-label={isPasswordVisible ? "Hide password" : "Show password"}
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            style={{
              position: "absolute",
              right: SPACING.S16,
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
              padding: SPACING.S0,
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <img
              src={isPasswordVisible ? closeEye : openEye}
              alt=""
              style={{ width: "18px", height: "18px", opacity: 0.65 }}
            />
          </button>
        ) : null}
      </div>

      {hasError ? (
        <p
          id={errorId}
          role="alert"
          style={{
            display: DISPLAY.FLEX,
            alignItems: ALIGN_ITEMS.CENTER,
            gap: SPACING.S6,
            margin: 0,
            marginTop: SPACING.S8,
            fontSize: FONT_SIZE.F12,
            fontFamily: FontFamily.ROBOTO_REGULAR,
            color: Colors.Tomato,
          }}
        >
          <ErrorIcon />
          {errorMessage}
        </p>
      ) : null}
    </div>
  );
};

export default AuthInput;
